import { useRemoveFollow } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionButton,
  Label,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  Navigate,
  useParams,
  useNavigate,
} from "@tanstack/react-router"
import {
  useCallback,
  useState,
} from "react"
import styles from "./ManageFollowing.module.scss"
import { StopFollowingDialog } from "./StopFollowingDialog"
import { PageTemplate } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useFollowById } from "src/hooks"
import { Nickname } from "src/pages/Account/AccountDetails/Nickname"
import { RemoveFollowErrorToast } from "src/toasts/RemoveFollowErrorToast"
import { RemoveFollowSuccessToast } from "src/toasts/RemoveFollowSuccessToast"
import {
  followToSharerFull,
  injectProps,
} from "src/utils"

const nameElementId = "name-element-id"
const polyglotPrefix = "pages.data_sharing.following.manage_following."

export const ManageFollowing = () => {
  const polyglot = usePolyglot()
  const navigate = useNavigate()

  const followId = useParams({
    from: "/private/data_sharing/following/$followId/manage_following",
    select: (params) => params.followId,
  })

  const [
    stopFollowingDialogOpen,
    setStopFollowingDialogOpen,
  ] = useState(false)

  const [
    follow,
    hasLoaded,
  ] = useFollowById(followId)
  const displayName = follow ? followToSharerFull(follow) : ""

  const removeFollow = useRemoveFollow()

  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const stopFollowing = useCallback(
    async () => {
      setIsOverlayLoaderVisible(true)

      try {
        if (!removeFollow) {
          throw new Error("removeFollow is not defined")
        }

        await removeFollow(followId)

        const ToastComponent = injectProps(
          RemoveFollowSuccessToast,
          {
            name: displayName,
          },
        )

        showToast(
          "remove-follow-success-toast",
          ToastComponent,
        )

        void navigate({ to: "/data_sharing/following" })
      } catch {
        showToast(
          "remove-follow-error-toast",
          RemoveFollowErrorToast,
        )
      }

      setIsOverlayLoaderVisible(false)
    },
    [
      displayName,
      showToast,
      removeFollow,
      followId,
      navigate,
      setIsOverlayLoaderVisible,
    ],
  )

  const onStopFollowingClick = useCallback(
    () => {
      setStopFollowingDialogOpen(true)
    },
    [],
  )

  if (hasLoaded && follow === undefined) {
    return (
      <Navigate
        to="/data_sharing/following"
        params={{}}
        search={{}}
        replace
      />
    )
  }

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.manage_following")}
      backRoutePath="/data_sharing/following"
    >
      <div className={styles.container}>
        <div className={styles.nameContainer}>
          <Label htmlFor={nameElementId}>
            {polyglot.t(`${polyglotPrefix}following_label`)}
          </Label>
          <div
            id={nameElementId}
            className={styles.name}
          >
            {displayName}
          </div>
        </div>

        <Nickname accountId={follow?.following_account_id ?? -1} />

        <div className={styles.buttonWrapper}>
          <ActionButton
            stylePreset="destructive"
            className={styles.stopFollowingButton}
            onClick={onStopFollowingClick}
            id="stop-following-btn"
          >
            {polyglot.t(`${polyglotPrefix}stop_following_cta`)}
          </ActionButton>
        </div>

        <StopFollowingDialog
          open={stopFollowingDialogOpen}
          onOpenChange={setStopFollowingDialogOpen}
          stopFollowing={stopFollowing}
          displayName={displayName}
        />
      </div>
    </PageTemplate>
  )
}
